// Family size-combination picker (migration 025): every catalogue
// combination of the product's family, browsable via a dropdown grouped
// by aspect ratio. The product's own combination is preselected and
// badged when the catalogue pins it; custom (PSS) sizes show the family
// table without a "this product" marker.
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { VariantTableData } from "@/lib/demoData";

interface Combo {
  aspect: string;
  row: Record<string, number | string | Record<string, number>>;
}

export function VariantTable({ table }: { table: VariantTableData }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "").toLowerCase().split(/[-_]/)[0];
  const locale = lang === "fr" || lang === "nl" ? lang : "en";

  // Flatten aspect groups into option entries, sorted by width within
  // each aspect, aspects ascending.
  const combos = useMemo<Combo[]>(() => {
    const out: Combo[] = [];
    for (const aspect of Object.keys(table.rows).sort(
      (a, b) => Number(a) - Number(b),
    )) {
      const rows = [...table.rows[aspect]!].sort(
        (a, b) => Number(a.viewing_width_mm) - Number(b.viewing_width_mm),
      );
      for (const row of rows) out.push({ aspect, row });
    }
    return out;
  }, [table]);

  const comboKey = (c: Combo) => `${c.aspect}|${c.row.viewing_width_mm}`;
  const currentKey = table.current
    ? `${table.current.aspect}|${table.current.viewing_width_mm}`
    : null;
  const [selected, setSelected] = useState<string>(
    currentKey ?? (combos[0] ? comboKey(combos[0]) : ""),
  );
  const combo =
    combos.find((c) => comboKey(c) === selected) ?? combos[0] ?? null;

  if (!combo) return null;

  const aspectLabel = (aspect: string) =>
    table.aspect_labels[aspect] ?? `${aspect}:1`;

  const priceFmt = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
  const formatCell = (
    col: string,
    value: number | string | Record<string, number>,
  ) =>
    col === "list_price_eur" && typeof value === "number"
      ? priceFmt.format(value)
      : typeof value === "object"
        ? "" // fabric matrix rides along but is never a displayed cell
        : String(value);
  const hasPrices = table.columns.includes("list_price_eur");

  return (
    <div>
      <h2 className="mb-2 text-xs font-510 uppercase tracking-wider text-fg-tertiary">
        {t("product.availableCombinations")}
      </h2>
      <div className="rounded-md border border-border bg-bg-surface p-4">
        <p className="mb-3 text-xs text-fg-tertiary">{table.title}</p>
        <select
          aria-label={t("product.availableCombinations")}
          className="mb-4 w-full rounded-md border border-border bg-bg-base px-3 py-2 text-sm text-fg-primary"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          {Object.keys(table.rows)
            .sort((a, b) => Number(a) - Number(b))
            .map((aspect) => (
              <optgroup key={aspect} label={aspectLabel(aspect)}>
                {combos
                  .filter((c) => c.aspect === aspect)
                  .map((c) => (
                    <option key={comboKey(c)} value={comboKey(c)}>
                      {`${c.row.viewing_width_mm} × ${c.row.viewing_height_mm} mm`}
                      {comboKey(c) === currentKey
                        ? ` — ${t("product.thisProduct")}`
                        : ""}
                    </option>
                  ))}
              </optgroup>
            ))}
        </select>
        <dl className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
          {table.columns.map((col) => {
            const meta = table.column_labels[col];
            const value = combo.row[col];
            if (value == null) return null;
            return (
              <div
                key={col}
                className="flex justify-between gap-4 border-b border-border/50 py-1.5 last:border-b-0"
              >
                <dt className="text-fg-tertiary">
                  {meta ? meta[locale] : col}
                  {meta?.unit ? ` (${meta.unit})` : ""}
                </dt>
                <dd className="text-right font-510 text-fg-primary">
                  {formatCell(col, value)}
                </dd>
              </div>
            );
          })}
        </dl>
        {hasPrices && (
          <p className="mt-3 text-[11px] text-fg-tertiary">
            {t("product.listPriceNote")}
          </p>
        )}
        <p className="mt-3 text-[11px] text-fg-tertiary">{table.source}</p>
      </div>
    </div>
  );
}
